import { IonPage } from '@ionic/react'
import { AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useGetUnitGenericDetails } from '@apps-common/apis/portal/unit'
import {
  ManagementNotes,
  PortalNotes,
  UnitNoteCreateButton
} from '@apps-common/apps/portal/pages/units/pages/manage/pages/notes/components'
import { AccountNoTitle, PageTitle } from '@apps-common/components/common/portal-title'
import { ButtonTab } from '@apps-common/components/elements/button'
import { Header, LayoutContainer, VertSpacerLg } from '@apps-common/components/layout'
import { Breadcrumbs, Navbar } from '@apps-common/components/navigation'
import { AUTH_CLAIMS, Authorization, useCheckAuthorization } from '@apps-common/lib'
import { ROUTE_UNIT_NOTES } from '@apps-radionet-portal/config/private-routes'

export type UnitNoteType = 'portal' | 'management'

export const UnitNotesPage: React.FC = () => {
  const { status, unitId } = useParams<{ status: string; unitId: string }>()
  const [tab, setTab] = useState<UnitNoteType>('portal')

  const { data: unit, isLoading: unitIsLoading, isError: unitIsError } = useGetUnitGenericDetails({ status, unitId })

  const { accountNo } = unit || {}

  const managementAccess = useCheckAuthorization({
    roles: AUTH_CLAIMS.managementAdmin.roles,
    scopes: AUTH_CLAIMS.managementAdmin.scopes
  })
  const loading = unitIsLoading || unitIsError

  return (
    <IonPage>
      <Header>
        <div className='max-w-[1100px]'>
          <Navbar showBack transparent icons={[]} />
          <Breadcrumbs route={ROUTE_UNIT_NOTES} />
          <div className='flex items-center justify-between'>
            <PageTitle classes='flex items-center justify-start gap-[10px]'>
              <span className='block'>Notes</span>
              <AccountNoTitle text={`(Acc# ${accountNo})`} />
            </PageTitle>
            <UnitNoteCreateButton type={tab} entityId={unitId} loading={loading} />
          </div>
          <VertSpacerLg />
          {managementAccess && (
            <div className='flex gap-[10px]'>
              <ButtonTab classes='w-full sm:w-auto sm:min-w-[160px]' active={tab === 'portal'} callback={() => setTab('portal')}>
                Portal
              </ButtonTab>
              <ButtonTab classes='w-full sm:w-auto sm:min-w-[160px]' active={tab === 'management'} callback={() => setTab('management')}>
                Management
              </ButtonTab>
            </div>
          )}
        </div>
      </Header>
      <LayoutContainer>
        <div className='max-w-full sm:max-w-[1000px] overflow-x-hidden'>
          <AnimatePresence mode='wait'>
            {tab === 'portal' && (
              <PortalNotes
                key='unit-portal-notes'
                paddingTop={managementAccess ? 'pt-[60px] md:pt-[70px]' : 'pt-[20px] md:pt-[20px]'}
                entityId={unitId}
              />
            )}
            {tab === 'management' && (
              <Authorization
                key='unit-management-notes'
                roles={AUTH_CLAIMS.managementAdmin.roles}
                scopes={AUTH_CLAIMS.managementAdmin.scopes}
              >
                <ManagementNotes entityId={unitId} />
              </Authorization>
            )}
          </AnimatePresence>
        </div>
      </LayoutContainer>
    </IonPage>
  )
}
